import { useMemo, useState } from "react";
import { useAnimals } from "../../../hooks/useAnimals";
import { usePayments } from "../../../hooks/usePayments";
import { getShareTotal } from "../../../lib/cascadeOps";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
  getAnimalSalesType,
} from "../../../lib/animalLabels";
import { exportRowsToCsv, type ExportColumn } from "../../../lib/excelExport";
import type { AnimalItem } from "../../../mocks/animals";

type AnimalTypeFilter = "Tümü" | "Büyükbaş" | "Küçükbaş";
type SalesTypeFilter = "Tümü" | "Hisseli" | "Hissesiz" | "Dış Kesim";

interface CompletedRow {
  id: number;
  kupeNo: string;
  siparisNo: string;
  tur: AnimalItem["tur"];
  satisTipi: "Hisseli" | "Hissesiz" | "Dış Kesim";
  kg?: number;
  hisse: number;
  satilan: number;
  toplamEtSatisi: number;
  brutToplam: number;
  toplamTahsilat: number;
  tamamlanmaTarihi: string;
  musteriler: string[];
  animal: AnimalItem;
}

interface ExportCompletedRow {
  kupeNo: string;
  siparisNo: string;
  tur: string;
  satisTipi: string;
  kg: string;
  hisse: number;
  satilan: number;
  toplamEtSatisi: number;
  brutToplam: number;
  toplamTahsilat: number;
  tamamlanmaTarihi: string;
  musteriler: string;
}

function fmtMoney(value: number) {
  return value.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
}

function latestPaymentDate(allDates: string[]) {
  const sorted = [...allDates].sort((a, b) => b.localeCompare(a));
  return sorted[0] ?? "";
}

function buildCompletedRows(
  animals: AnimalItem[],
  paymentDatesByAnimal: Map<number, string[]>
): CompletedRow[] {
  return animals
    .filter((animal) => animal.tamamlandi || animal.durum === "Teslim Edildi")
    .map((animal) => {
      const toplamEtSatisi = animal.hisseler.reduce(
        (sum, share) => sum + (share.tutar ?? 0),
        0
      );

      const brutToplam = animal.hisseler.reduce(
        (sum, share) => sum + getShareTotal(share),
        0
      );

      const toplamTahsilat = animal.hisseler.reduce(
        (sum, share) => sum + (share.odenen ?? 0),
        0
      );

      const musteriler = Array.from(
        new Set(
          animal.hisseler
            .map((share) => share.musteriAdi?.trim())
            .filter((value): value is string => Boolean(value))
        )
      );

      return {
        id: animal.id,
        kupeNo: getDisplayKupeNo(animal),
        siparisNo: getDisplaySiparisNo(animal),
        tur: animal.tur,
        satisTipi: getAnimalSalesType(animal),
        kg: animal.kg,
        hisse: animal.toplamHisse,
        satilan: animal.satirlanHisse,
        toplamEtSatisi,
        brutToplam,
        toplamTahsilat,
        tamamlanmaTarihi: latestPaymentDate(
          paymentDatesByAnimal.get(animal.id) ?? []
        ),
        musteriler,
        animal,
      };
    })
    .sort((a, b) => {
      const aDate = a.tamamlanmaTarihi || "";
      const bDate = b.tamamlanmaTarihi || "";
      return (
        bDate.localeCompare(aDate) ||
        a.kupeNo.localeCompare(b.kupeNo, "tr", {
          numeric: true,
          sensitivity: "base",
        })
      );
    });
}

export default function TamamlananPage() {
  const { animals } = useAnimals();
  const { payments } = usePayments();

  const [kupeSearch, setKupeSearch] = useState("");
  const [siparisSearch, setSiparisSearch] = useState("");
  const [musteriSearch, setMusteriSearch] = useState("");
  const [turFilter, setTurFilter] = useState<AnimalTypeFilter>("Tümü");
  const [salesTypeFilter, setSalesTypeFilter] =
    useState<SalesTypeFilter>("Tümü");
  const [tarihBas, setTarihBas] = useState("");
  const [tarihSon, setTarihSon] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const paymentDatesByAnimal = useMemo(() => {
    const map = new Map<number, string[]>();

    for (const payment of payments) {
      const current = map.get(payment.animalId) ?? [];
      current.push(payment.tarih);
      map.set(payment.animalId, current);
    }

    return map;
  }, [payments]);

  const rows = useMemo(
    () => buildCompletedRows(animals, paymentDatesByAnimal),
    [animals, paymentDatesByAnimal]
  );

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (kupeSearch.trim()) {
        const q = kupeSearch.toLocaleLowerCase("tr-TR");
        if (!row.kupeNo.toLocaleLowerCase("tr-TR").includes(q)) return false;
      }

      if (siparisSearch.trim()) {
        const q = siparisSearch.toLocaleLowerCase("tr-TR");
        if (!row.siparisNo.toLocaleLowerCase("tr-TR").includes(q)) return false;
      }

      if (musteriSearch.trim()) {
        const q = musteriSearch.toLocaleLowerCase("tr-TR");
        const found = row.musteriler.some((name) =>
          name.toLocaleLowerCase("tr-TR").includes(q)
        );
        if (!found) return false;
      }

      if (turFilter !== "Tümü" && row.tur !== turFilter) return false;
      if (salesTypeFilter !== "Tümü" && row.satisTipi !== salesTypeFilter) return false;
      if (tarihBas && row.tamamlanmaTarihi && row.tamamlanmaTarihi < tarihBas) {
        return false;
      }
      if (tarihSon && row.tamamlanmaTarihi && row.tamamlanmaTarihi > tarihSon) {
        return false;
      }

      return true;
    });
  }, [
    rows,
    kupeSearch,
    siparisSearch,
    musteriSearch,
    turFilter,
    salesTypeFilter,
    tarihBas,
    tarihSon,
  ]);

  const stats = useMemo(() => {
    return {
      toplamHayvan: filtered.length,
      toplamEtSatisi: filtered.reduce((sum, row) => sum + row.toplamEtSatisi, 0),
      brutToplam: filtered.reduce((sum, row) => sum + row.brutToplam, 0),
      toplamTahsilat: filtered.reduce((sum, row) => sum + row.toplamTahsilat, 0),
    };
  }, [filtered]);

  const selected = filtered.find((row) => row.id === selectedId) ?? null;

  function exportCurrent() {
    const exportRows: ExportCompletedRow[] = filtered.map((row) => ({
      kupeNo: row.kupeNo,
      siparisNo: row.siparisNo,
      tur: row.tur,
      satisTipi: row.satisTipi,
      kg: row.kg ? `${row.kg}` : "",
      hisse: row.hisse,
      satilan: row.satilan,
      toplamEtSatisi: row.toplamEtSatisi,
      brutToplam: row.brutToplam,
      toplamTahsilat: row.toplamTahsilat,
      tamamlanmaTarihi: row.tamamlanmaTarihi,
      musteriler: row.musteriler.join(" | "),
    }));

    const columns: ExportColumn<ExportCompletedRow>[] = [
      { key: "kupeNo", label: "Küpe No" },
      { key: "siparisNo", label: "Sipariş No" },
      { key: "tur", label: "Tür" },
      { key: "satisTipi", label: "Satış Tipi" },
      { key: "kg", label: "Kg" },
      { key: "hisse", label: "Toplam Hisse" },
      { key: "satilan", label: "Satılan" },
      { key: "toplamEtSatisi", label: "Et Satışı" },
      { key: "brutToplam", label: "Brüt Toplam" },
      { key: "toplamTahsilat", label: "Tahsilat" },
      { key: "tamamlanmaTarihi", label: "Tamamlanma Tarihi" },
      { key: "musteriler", label: "Müşteriler" },
    ];

    exportRowsToCsv(
      `tamamlananlar-${new Date().toISOString().slice(0, 10)}`,
      exportRows,
      columns
    );
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-6xl mx-auto px-3 sm:px-5 py-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-base text-farm-brown">
              Tamamlananlar
            </h1>
            <p className="text-xs text-farm-muted">
              Tamamlanan veya teslim edilmiş hayvanların arşiv ekranı
            </p>
          </div>

          <button
            type="button"
            onClick={exportCurrent}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:border-farm-earth/40"
          >
            <i className="ri-file-excel-2-line text-base" />
            Excel Çıktısı
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            {
              label: "Tamamlanan Hayvan",
              value: String(stats.toplamHayvan),
              icon: "ri-checkbox-circle-line",
              color: "text-farm-green",
            },
            {
              label: "Et Satış Toplamı",
              value: fmtMoney(stats.toplamEtSatisi),
              icon: "ri-scales-2-line",
              color: "text-farm-earth",
            },
            {
              label: "Brüt Toplam",
              value: fmtMoney(stats.brutToplam),
              icon: "ri-money-dollar-circle-line",
              color: "text-farm-gold",
            },
            {
              label: "Toplam Tahsilat",
              value: fmtMoney(stats.toplamTahsilat),
              icon: "ri-bank-card-line",
              color: "text-farm-green",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5 flex items-center gap-2"
            >
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-lg bg-farm-cream ${item.color} flex-shrink-0`}
              >
                <i className={`${item.icon} text-sm`} />
              </div>
              <div className="min-w-0">
                <p className={`font-bold text-sm ${item.color} truncate`}>
                  {item.value}
                </p>
                <p className="text-[10px] text-farm-muted">{item.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-farm-warm border border-farm-border rounded-farm p-3 space-y-3">
          <div className="grid md:grid-cols-3 gap-2">
            <input
              value={kupeSearch}
              onChange={(e) => setKupeSearch(e.target.value)}
              placeholder="Küpe No ara..."
              className="w-full px-3 py-2 text-sm border border-farm-border rounded-lg bg-farm-cream outline-none focus:border-farm-earth"
            />
            <input
              value={siparisSearch}
              onChange={(e) => setSiparisSearch(e.target.value)}
              placeholder="Sipariş No ara..."
              className="w-full px-3 py-2 text-sm border border-farm-border rounded-lg bg-farm-cream outline-none focus:border-farm-earth"
            />
            <input
              value={musteriSearch}
              onChange={(e) => setMusteriSearch(e.target.value)}
              placeholder="Müşteri ara..."
              className="w-full px-3 py-2 text-sm border border-farm-border rounded-lg bg-farm-cream outline-none focus:border-farm-earth"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {(["Tümü", "Büyükbaş", "Küçükbaş"] as const).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setTurFilter(item)}
                className={`px-3 py-2 rounded-full border text-xs font-semibold transition-colors ${
                  turFilter === item
                    ? "bg-farm-earth text-white border-farm-earth"
                    : "bg-farm-cream text-farm-muted border-farm-border hover:border-farm-earth/40"
                }`}
              >
                {item}
              </button>
            ))}

            {(["Tümü", "Hisseli", "Hissesiz"] as const).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setSalesTypeFilter(item)}
                className={`px-3 py-2 rounded-full border text-xs font-semibold transition-colors ${
                  salesTypeFilter === item
                    ? "bg-farm-brown text-white border-farm-brown"
                    : "bg-farm-cream text-farm-muted border-farm-border hover:border-farm-brown/40"
                }`}
              >
                {item}
              </button>
            ))}

            <input
              type="date"
              value={tarihBas}
              onChange={(e) => setTarihBas(e.target.value)}
              className="px-3 py-2 text-xs border border-farm-border rounded-lg bg-farm-cream outline-none"
            />
            <input
              type="date"
              value={tarihSon}
              onChange={(e) => setTarihSon(e.target.value)}
              className="px-3 py-2 text-xs border border-farm-border rounded-lg bg-farm-cream outline-none"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-farm-warm border border-farm-border rounded-farm py-14 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto text-farm-muted mb-3">
              <i className="ri-archive-line text-4xl" />
            </div>
            <p className="text-sm text-farm-muted font-medium">
              Arşivde gösterilecek kayıt yok
            </p>
          </div>
        ) : (
          <div className="bg-farm-warm border border-farm-border rounded-farm overflow-hidden">
            <div
              className="hidden lg:grid bg-farm-cream border-b border-farm-border text-[10px] font-semibold text-farm-muted uppercase tracking-wide"
              style={{
                gridTemplateColumns:
                  "60px 90px 120px 90px 80px 70px 120px 120px 120px 110px 90px",
              }}
            >
              <div className="px-3 py-2">#</div>
              <div className="px-3 py-2">Küpe No</div>
              <div className="px-3 py-2">Sipariş No</div>
              <div className="px-3 py-2">Tür / Satış</div>
              <div className="px-3 py-2">Kg</div>
              <div className="px-3 py-2">Hisse</div>
              <div className="px-3 py-2 text-right">Et Satışı</div>
              <div className="px-3 py-2 text-right">Brüt</div>
              <div className="px-3 py-2 text-right">Tahsilat</div>
              <div className="px-3 py-2">Tamamlanma</div>
              <div className="px-3 py-2 text-center">İşlem</div>
            </div>

            {filtered.map((row) => (
              <div
                key={row.id}
                className="border-b border-farm-border last:border-b-0"
              >
                <div
                  className="hidden lg:grid items-center"
                  style={{
                    gridTemplateColumns:
                      "60px 90px 120px 90px 80px 70px 120px 120px 120px 110px 90px",
                  }}
                >
                  <div className="px-3 py-3 text-sm text-farm-muted">#{row.id}</div>
                  <div className="px-3 py-3">
                    <p className="font-mono text-sm font-bold text-farm-brown">
                      {row.kupeNo}
                    </p>
                  </div>
                  <div className="px-3 py-3">
                    <p className="text-xs font-semibold text-farm-brown">
                      {row.siparisNo}
                    </p>
                  </div>
                  <div className="px-3 py-3 text-sm text-farm-muted">
                    {row.tur}
                    <div className="text-[11px] text-farm-earth mt-0.5">
                      {row.satisTipi}
                    </div>
                  </div>
                  <div className="px-3 py-3 text-sm text-farm-muted">
                    {row.kg ?? "—"} kg
                  </div>
                  <div className="px-3 py-3 text-sm text-farm-muted">
                    {row.satilan}/{row.hisse}
                  </div>
                  <div className="px-3 py-3 text-right text-sm font-semibold text-farm-brown">
                    {fmtMoney(row.toplamEtSatisi)}
                  </div>
                  <div className="px-3 py-3 text-right text-sm font-semibold text-farm-brown">
                    {fmtMoney(row.brutToplam)}
                  </div>
                  <div className="px-3 py-3 text-right text-sm font-semibold text-farm-green">
                    {fmtMoney(row.toplamTahsilat)}
                  </div>
                  <div className="px-3 py-3 text-xs text-farm-muted">
                    {row.tamamlanmaTarihi || "—"}
                  </div>
                  <div className="px-3 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => setSelectedId(row.id)}
                      className="px-3 py-1 rounded-full border border-farm-earth/20 bg-farm-earth-pale text-farm-earth text-xs font-semibold hover:bg-farm-earth hover:text-white transition-colors"
                    >
                      Detay
                    </button>
                  </div>
                </div>

                <div className="lg:hidden px-3 py-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-mono text-sm font-bold text-farm-brown">
                        {row.kupeNo}
                      </p>
                      <p className="text-xs text-farm-muted">
                        {row.siparisNo}
                      </p>
                      <p className="text-xs text-farm-muted mt-0.5">
                        {row.tur} · {row.satisTipi} · {row.kg ?? "—"} kg
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedId(row.id)}
                      className="px-3 py-1 rounded-full border border-farm-earth/20 bg-farm-earth-pale text-farm-earth text-xs font-semibold"
                    >
                      Detay
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-farm-cream rounded px-2 py-2">
                      <p className="text-farm-muted">Brüt</p>
                      <p className="font-semibold text-farm-brown">
                        {fmtMoney(row.brutToplam)}
                      </p>
                    </div>
                    <div className="bg-farm-cream rounded px-2 py-2">
                      <p className="text-farm-muted">Tahsilat</p>
                      <p className="font-semibold text-farm-green">
                        {fmtMoney(row.toplamTahsilat)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-3">
          <div className="w-full max-w-3xl bg-farm-cream border border-farm-border rounded-farm overflow-hidden">
            <div className="px-4 py-3 border-b border-farm-border bg-farm-warm flex items-center justify-between">
              <div>
                <p className="font-heading font-semibold text-farm-brown">
                  {selected.kupeNo}
                </p>
                <p className="text-xs text-farm-muted">
                  Sipariş No: {selected.siparisNo}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-farm-muted hover:bg-farm-border"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-4 space-y-3 max-h-[75vh] overflow-y-auto">
              <div className="grid sm:grid-cols-4 gap-2">
                <div className="bg-farm-warm rounded-farm px-3 py-2">
                  <p className="text-[10px] text-farm-muted">Et Satışı</p>
                  <p className="text-sm font-semibold text-farm-brown">
                    {fmtMoney(selected.toplamEtSatisi)}
                  </p>
                </div>
                <div className="bg-farm-warm rounded-farm px-3 py-2">
                  <p className="text-[10px] text-farm-muted">Brüt</p>
                  <p className="text-sm font-semibold text-farm-brown">
                    {fmtMoney(selected.brutToplam)}
                  </p>
                </div>
                <div className="bg-farm-warm rounded-farm px-3 py-2">
                  <p className="text-[10px] text-farm-muted">Tahsilat</p>
                  <p className="text-sm font-semibold text-farm-green">
                    {fmtMoney(selected.toplamTahsilat)}
                  </p>
                </div>
                <div className="bg-farm-warm rounded-farm px-3 py-2">
                  <p className="text-[10px] text-farm-muted">Tamamlanma</p>
                  <p className="text-sm font-semibold text-farm-brown">
                    {selected.tamamlanmaTarihi || "—"}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {selected.animal.hisseler.map((share) => (
                  <div
                    key={share.id}
                    className="bg-farm-warm border border-farm-border rounded-farm px-3 py-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-farm-brown">
                          {share.musteriAdi || "İsimsiz kayıt"}
                        </p>
                        <p className="text-xs text-farm-muted">
                          {share.hisseNo} · {share.hisseSayisi} hisse
                        </p>
                      </div>
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
                        {share.durum}
                      </span>
                    </div>

                    <div className="grid sm:grid-cols-4 gap-2 mt-3 text-xs">
                      <div className="bg-farm-cream rounded px-2 py-2">
                        <p className="text-farm-muted">Et</p>
                        <p className="font-semibold text-farm-brown">
                          {fmtMoney(share.tutar)}
                        </p>
                      </div>
                      <div className="bg-farm-cream rounded px-2 py-2">
                        <p className="text-farm-muted">Toplam</p>
                        <p className="font-semibold text-farm-brown">
                          {fmtMoney(getShareTotal(share))}
                        </p>
                      </div>
                      <div className="bg-farm-cream rounded px-2 py-2">
                        <p className="text-farm-muted">Ödenen</p>
                        <p className="font-semibold text-farm-green">
                          {fmtMoney(share.odenen)}
                        </p>
                      </div>
                      <div className="bg-farm-cream rounded px-2 py-2">
                        <p className="text-farm-muted">Kalan</p>
                        <p className="font-semibold text-farm-earth">
                          {fmtMoney(share.kalan)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selected.musteriler.length > 0 && (
                <div className="bg-farm-warm border border-farm-border rounded-farm px-3 py-3">
                  <p className="text-[10px] text-farm-muted uppercase tracking-wide">
                    Müşteriler
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selected.musteriler.map((name) => (
                      <span
                        key={name}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border border-farm-border bg-farm-cream text-farm-brown"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}